import React, { useState, useEffect } from "react";
import DishForm1, { DishForm1Data } from "./DishForm1";
import DishForm2, { DishForm2Data } from "./DishForm2";
import DishForm3, { DishForm3Data } from "./DishForm3";
import DishForm4, { DishForm4Data } from "./DishForm4";
import DishForm5, { DishForm5Data } from "./DishForm5";
import { Category } from "../../utils/types";
import { categoryService, dishService } from "../../services";
import { toast } from "react-toastify";
import { useTheme } from "../../context/ThemeContext";

interface DishData {
  dishName: string;
  categoryId: string;
  subCategoryId: string;
  description: string;
  shortDescription: string;
  images: {
    url: string;
    isPrimary: boolean;
    sortOrder: number;
  }[];
  foodTypeId: string;
  cuisineTypeId: string;
  mealTypeIds: string[];
  spiceLevelId: string;
  tagIds: string[];
  allergenIds: string[];
  dietaryLabelIds: string[];
  pricingModel: string;
  basePrice: number;
  comparePrice: number;
  gstRate: number;
  gstInclusive: boolean;
  packagingCharge: number;
  isAvailable: boolean;
  preparationTime: number;
  maxQuantityPerOrder: number;
  serves: string;
  calories: number;
  isRecommended: boolean;
  sortOrder: number;
  availableForDelivery: boolean;
  availableForDineIn: boolean;
  availableOnQRMenu: boolean;
}

interface AddDishProps {
  dishId?: string;
  onComplete?: (data: DishData) => void;
  onCancel?: () => void;
}

const AddDish: React.FC<AddDishProps> = ({ dishId, onComplete, onCancel }) => {
  const { isDarkMode } = useTheme();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  
  // Complete dish data state
  const [dishData, setDishData] = useState<DishData>({
    dishName: "",
    categoryId: "",
    subCategoryId: "",
    description: "",
    shortDescription: "",
    images: [],
    foodTypeId: "",
    cuisineTypeId: "",
    mealTypeIds: [],
    spiceLevelId: "",
    tagIds: [],
    allergenIds: [],
    dietaryLabelIds: [],
    pricingModel: "Fixed Price",
    basePrice: 0,
    comparePrice: 0,
    gstRate: 0,
    gstInclusive: false,
    packagingCharge: 0,
    isAvailable: false,
    preparationTime: 0,
    maxQuantityPerOrder: 0,
    serves: "",
    calories: 0,
    isRecommended: false,
    sortOrder: 0,
    availableForDelivery: false,
    availableForDineIn: false,
    availableOnQRMenu: false,
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response: any = await categoryService.getCategories();
        console.log("Categories response:", response);
        setCategories(response.data);
      } catch (error: any) {
        console.error("Error fetching categories:", error);
        toast.error(error.message || "Failed to load categories");
      }
    };

    fetchCategories();
  }, []);

  // Fetch dish data if editing
  useEffect(() => {
    const fetchDishData = async () => {
      if (!dishId) return;
      
      setLoading(true);
      try {
        const response: any = await dishService.getDishById(dishId);
        console.log("Dish data response:", response);
        
        if (response.status && response.data) {
          const dish = response.data;
          setDishData({
            dishName: dish.dishName || "",
            categoryId: dish.categoryId || "",
            subCategoryId: dish.subCategoryId || "",
            description: dish.description || "",
            shortDescription: dish.shortDescription || "",
            images: dish.images || [],
            foodTypeId: dish.foodTypeId || "",
            cuisineTypeId: dish.cuisineTypeId || "",
            mealTypeIds: dish.mealTypeIds || [],
            spiceLevelId: dish.spiceLevelId || "",
            tagIds: dish.tagIds || [],
            allergenIds: dish.allergenIds || [],
            dietaryLabelIds: dish.dietaryLabelIds || [],
            pricingModel: dish.pricingModel || "Fixed Price",
            basePrice: dish.basePrice || 0,
            comparePrice: dish.comparePrice || 0,
            gstRate: dish.gstRate || 0,
            gstInclusive: dish.gstInclusive || false,
            packagingCharge: dish.packagingCharge || 0,
            isAvailable: dish.isAvailable || false,
            preparationTime: dish.preparationTime || 0,
            maxQuantityPerOrder: dish.maxQuantityPerOrder || 0,
            serves: dish.serves || "",
            calories: dish.calories || 0,
            isRecommended: dish.isRecommended || false,
            sortOrder: dish.sortOrder || 0,
            availableForDelivery: dish.availableForDelivery || false,
            availableForDineIn: dish.availableForDineIn || false,
            availableOnQRMenu: dish.availableOnQRMenu || false,
          });
        }
      } catch (error: any) {
        console.error("Error fetching dish data:", error);
        toast.error(error.message || "Failed to load dish data");
      } finally {
        setLoading(false);
      }
    };

    fetchDishData();
  }, [dishId]);

  const handleNext = (step: number, data: any) => {
    // Just move to next step, data is already updated in dishData
    setCurrentStep(step + 1);
  };

  // Handle real-time updates from Form 1
  const handleForm1Change = (data: Partial<DishForm1Data>) => {
    setDishData((prev) => {
      const updated = {
        ...prev,
        dishName: data.dishName ?? prev.dishName,
        categoryId: data.category ?? prev.categoryId,
        subCategoryId: data.subCategoryId ?? prev.subCategoryId,
        description: data.description ?? prev.description,
        shortDescription: data.shortDescription ?? prev.shortDescription,
        images: data.images ?? prev.images,
      };
      return updated;
    });
  };

  // Handle real-time updates from Form 2
  const handleForm2Change = (data: Partial<DishForm2Data>) => {
    setDishData((prev) => ({
      ...prev,
      foodTypeId: data.foodType ?? prev.foodTypeId,
      cuisineTypeId: data.cuisineType ?? prev.cuisineTypeId,
      mealTypeIds: data.mealType ? [data.mealType] : prev.mealTypeIds,
      spiceLevelId: data.spiceLevel ?? prev.spiceLevelId,
      tagIds: data.tags ?? prev.tagIds,
      allergenIds: data.allergenInfo ?? prev.allergenIds,
      dietaryLabelIds: data.dietaryLabels ?? prev.dietaryLabelIds,
    }));
  };

  // Handle real-time updates from Form 3
  const handleForm3Change = (data: Partial<DishForm3Data>) => {
    setDishData((prev) => ({
      ...prev,
      pricingModel: data.pricingModel ?? prev.pricingModel,
      basePrice: data.basePrice !== undefined ? (data.basePrice.trim() ? parseFloat(data.basePrice) : 0) : prev.basePrice,
      comparePrice: data.comparePrice !== undefined ? (data.comparePrice.trim() ? parseFloat(data.comparePrice) : 0) : prev.comparePrice,
      gstRate: data.gstRate !== undefined ? (data.gstRate.trim() ? parseFloat(data.gstRate.replace('%', '')) : 0) : prev.gstRate,
      gstInclusive: data.gstInclusive ?? prev.gstInclusive,
      packagingCharge: data.packagingCharge !== undefined ? (data.packagingCharge.trim() ? parseFloat(data.packagingCharge) : 0) : prev.packagingCharge,
    }));
  };

  // Handle real-time updates from Form 4
  const handleForm4Change = (data: Partial<DishForm4Data>) => {
    setDishData((prev) => ({
      ...prev,
      isAvailable: data.isAvailable ?? prev.isAvailable,
      preparationTime: data.preparationTime !== undefined ? (data.preparationTime.trim() ? parseInt(data.preparationTime) : 0) : prev.preparationTime,
      serves: data.serves ?? prev.serves,
      calories: data.calories !== undefined ? (data.calories.trim() ? parseInt(data.calories) : 0) : prev.calories,
      isRecommended: data.isRecommended ?? prev.isRecommended,
      sortOrder: data.sortOrder !== undefined ? (data.sortOrder.trim() ? parseInt(data.sortOrder) : 0) : prev.sortOrder,
      maxQuantityPerOrder: data.maxQuantityPerOrder !== undefined ? (String(data.maxQuantityPerOrder).trim() ? parseInt(String(data.maxQuantityPerOrder)) : 0) : prev.maxQuantityPerOrder,
    }));
  };

  // Handle real-time updates from Form 5
  const handleForm5Change = (data: Partial<DishForm5Data>) => {
    setDishData((prev) => ({
      ...prev,
      availableForDelivery: data.availableForDelivery ?? prev.availableForDelivery,
      availableForDineIn: data.availableForDineIn ?? prev.availableForDineIn,
      availableOnQRMenu: data.availableOnQRMenu ?? prev.availableOnQRMenu,
    }));
  };

  // Handle form submission (Form 5 final step)
  const handleForm5Submit = () => {
    handleSubmitDish(dishData);
  };

  const handleSubmitDish = async (finalData: DishData) => {
    setLoading(true);
    try {
      let response: any;
      
      if (dishId) {
        // Update existing dish
        response = await dishService.updateDish(dishId, finalData);
      } else {
        // Create new dish
        response = await dishService.createDish(finalData);
      }
      
      if (response.status) {
        console.log(`Dish ${dishId ? 'updated' : 'created'} successfully:`, response);
        toast.success(response.message || `Dish ${dishId ? 'updated' : 'created'} successfully!`);
        // Call onComplete callback if provided
        if (onComplete) {
          onComplete(finalData);
        }
      } else {
        console.error(`Failed to ${dishId ? 'update' : 'create'} dish:`, response.message);
        toast.error(response.message || `Failed to ${dishId ? 'update' : 'create'} dish`);
      }
    } catch (error: any) {
      console.error(`Error ${dishId ? 'updating' : 'creating'} dish:`, error);
      toast.error(error.message || `Error ${dishId ? 'updating' : 'creating'} dish`);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };



  return (
    <div className={`min-h-screen pt-0 pb-8 px-3 sm:px-4 md:px-6 ${isDarkMode ? 'bg-gray-900' : 'bg-[#F9F9F9]'}`}>
      <div className="max-w-[720px] mx-auto">
        {/* Header with Progress */}
        <div className={`rounded-lg p-4 sm:p-6 mb-4 sm:mb-6 shadow-sm ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={onCancel}
              className={`flex items-center gap-1 font-Urbanist text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-[#6A6A6A]'}`}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M15 19l-7-7 7-7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back
            </button>
            <span className={`font-Urbanist text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
              {dishId ? "Edit Dish" : "Add Dish"}
            </span>
            <span className="w-12" />
          </div>
          {/* Progress Section */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-6">
            {/* Step Info */}
            <div className="flex-shrink-0">
              <div className={`font-Urbanist text-xs sm:text-[13px] font-normal mb-1 leading-tight sm:leading-[19px] ${isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'}`}>
                {dishId ? "Edit Dish - " : ""}Step {currentStep}
              </div>
              <div className={`font-Urbanist text-lg sm:text-[20px] font-medium leading-tight sm:leading-[25px] ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
                {currentStep === 1 && "Basic Info"}
                {currentStep === 2 && "Food Details"}
                {currentStep === 3 && "Pricing"}
                {currentStep === 4 && "Operations"}
                {currentStep === 5 && "Channel Availability"}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <div className={`flex-1 sm:flex-none sm:w-[200px] md:w-[272px] rounded-full h-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <div
                  className="bg-[#FF8A00] h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / 5) * 100}%` }}
                ></div>
              </div>
          
              {/* Percentage */}
              <div className="flex-shrink-0">
                <span className={`font-Urbanist text-lg sm:text-[20px] font-medium leading-tight sm:leading-[25px] ${isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'}`}>
                  {Math.round((currentStep / 5) * 100)}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Form Steps */}
        {currentStep === 1 && (
          <DishForm1
            key={dishId || 'new-dish'}
            onNext={(data) => handleNext(1, data)}
            onChange={handleForm1Change}
            initialData={{
              dishName: dishData.dishName,
              category: dishData.categoryId,
              subCategoryId: dishData.subCategoryId,
              description: dishData.description,
              shortDescription: dishData.shortDescription,
              images: dishData.images,
            }}
            categories={categories}
          />
        )}

        {currentStep === 2 && (
          <DishForm2
            key={dishId || 'new-dish'}
            onNext={(data) => handleNext(2, data)}
            onPrevious={handlePrevious}
            onChange={handleForm2Change}
            initialData={{
              foodType: dishData.foodTypeId as "veg" | "non-veg" | "egg" | "",
              cuisineType: dishData.cuisineTypeId,
              mealType: (dishData.mealTypeIds[0] as "breakfast" | "lunch" | "snacks" | "dinner" | "") || "",
              spiceLevel: dishData.spiceLevelId as "mild" | "medium" | "hot" | "extra-hot" | "",
              tags: dishData.tagIds,
              allergenInfo: dishData.allergenIds,
              dietaryLabels: dishData.dietaryLabelIds,
            }}
          />
        )}

        {currentStep === 3 && (
          <DishForm3
            key={dishId || 'new-dish'}
            onNext={(data) => handleNext(3, data)}
            onPrevious={handlePrevious}
            onChange={handleForm3Change}
            initialData={{
              pricingModel: dishData.pricingModel as "Fixed Price" | "Variant Based",
              basePrice: dishData.basePrice ? dishData.basePrice.toString() : "",
              comparePrice: dishData.comparePrice ? dishData.comparePrice.toString() : "",
              gstInclusive: dishData.gstInclusive,
              gstRate: dishData.gstRate ? `${dishData.gstRate}%` : "",
              packagingCharge: dishData.packagingCharge ? dishData.packagingCharge.toString() : "",
            }}
          />
        )}

        {currentStep === 4 && (
          <DishForm4
            key={dishId || 'new-dish'}
            onNext={(data) => handleNext(4, data)}
            onPrevious={handlePrevious}
            onChange={handleForm4Change}
            initialData={{
              isAvailable: dishData.isAvailable,
              preparationTime: dishData.preparationTime ? dishData.preparationTime.toString() : "",
              serves: dishData.serves,
              calories: dishData.calories ? dishData.calories.toString() : "",
              isRecommended: dishData.isRecommended,
              sortOrder: dishData.sortOrder ? dishData.sortOrder.toString() : "",
              maxQuantityPerOrder: dishData.maxQuantityPerOrder || "",
            }}
          />
        )}

        {currentStep === 5 && (
          <DishForm5
            key={dishId || 'new-dish'}
            onNext={handleForm5Submit}
            onPrevious={handlePrevious}
            onChange={handleForm5Change}
            initialData={{
              availableForDelivery: dishData.availableForDelivery,
              availableForDineIn: dishData.availableForDineIn,
              availableOnQRMenu: dishData.availableOnQRMenu,
              availabilitySchedule: "",
              availableDays: [],
            }}
          />
        )}

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
            <div className={`rounded-lg p-6 sm:p-8 flex flex-col items-center max-w-xs sm:max-w-sm mx-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-[#FF8A00] mb-3 sm:mb-4"></div>
              <p className={`font-Urbanist text-sm sm:text-[16px] text-center ${isDarkMode ? 'text-white' : 'text-[#050505]'}`}>
                {dishId ? "Updating dish..." : "Creating dish..."}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddDish;
